import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Container,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import SignatureCanvas from 'react-signature-canvas'
import { format } from 'date-fns'
import { ConsentService } from '@/services/consentService'
import { fetchUserIpAddress } from '@/utils/ipAddress'
import LOGO from '@/assets/pillarThera.png'
import api from '@/services/api'

interface ConsentSection {
  id: string
  title: string
  content: string[]
}

const CONSENT_SECTIONS: ConsentSection[] = [
  {
    id: 'authorization-access',
    title: '1. Authorization to Access Patient Information',
    content: [
      'I understand I may view therapy schedules, notes, invoices, messages, and other care‑related information.',
    ],
  },
  {
    id: 'hipaa-compliant',
    title: '2. HIPAA-Compliant Permission',
    content: [
      'The Patient authorizes PillarThera to disclose relevant PHI to me solely for care coordination purposes.',
    ],
  },
  {
    id: 'non-clinical-role',
    title: '3. Non‑Clinical Role',
    content: [
      'I understand I cannot make clinical decisions unless legally designated as a healthcare proxy.',
    ],
  },
  {
    id: 'confidentiality',
    title: '4. Confidentiality & Account Responsibility',
    content: [
      'I agree to maintain confidentiality, use my own login credentials, and follow platform rules.',
    ],
  },
  {
    id: 'revocation',
    title: '5. Revocation',
    content: ['The Patient may revoke my access at any time.'],
  },
  {
    id: 'no-financial-liability',
    title: '6. No Financial Liability',
    content: [
      `I am not financially responsible for the Patient's care or charges.`,
    ],
  },
]

const FamilyMemberConsentPage: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const familySignaturePadRef = useRef<SignatureCanvas>(null)
  const patientSignaturePadRef = useRef<SignatureCanvas>(null)

  // Form state
  const [hasReadAndAgreed, setHasReadAndAgreed] = useState(false)
  const [familySignature, setFamilySignature] = useState<string | null>(null)
  const [patientSignature, setPatientSignature] = useState<string | null>(null)
  const [familyMemberName, setFamilyMemberName] = useState('')
  const [patientName, setPatientName] = useState('')

  // API state
  const [ipAddress, setIpAddress] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isFetchingIp, setIsFetchingIp] = useState(true)
  const [isFetchingPatient, setIsFetchingPatient] = useState(true)

  const dateOfConsent = format(new Date(), 'MM/dd/yyyy')

  useEffect(() => {
    // Fetch IP address
    fetchUserIpAddress()
      .then(ip => {
        setIpAddress(ip)
        setIsFetchingIp(false)
      })
      .catch(() => {
        setIpAddress('Unknown')
        setIsFetchingIp(false)
      })

    // Fetch patient information
    const fetchPatientData = async () => {
      if (!patientId) {
        setIsFetchingPatient(false)
        setError('Invalid patient ID')
        return
      }

      try {
        const response = await api.get(`/patient/get-patient/${patientId}`)

        // Response structure: { message: string, data: { firstName, lastName, ... } }
        const patientData = response.data?.data

        if (!patientData) {
          setError('Patient data not found. Please contact support.')
          setIsFetchingPatient(false)
          return
        }

        // Extract patient name
        const firstName = patientData.firstName || ''
        const lastName = patientData.lastName || ''
        const fullName = `${firstName} ${lastName}`.trim()

        if (!fullName) {
          setError('Patient name not found. Please contact support.')
          setIsFetchingPatient(false)
          return
        }

        // Fill both fields with patient name
        // Family Member Name: read-only, filled with patient name
        setFamilyMemberName(fullName)
        // Patient Name: editable, pre-filled with patient name
        setPatientName(fullName)
      } catch (err: any) {
        console.error('Error fetching patient data:', err)
        setError(
          err.response?.data?.message ||
            'Failed to load patient information. Please refresh the page.'
        )
      } finally {
        setIsFetchingPatient(false)
      }
    }

    fetchPatientData()
  }, [patientId])

  const handleClearFamilySignature = () => {
    if (familySignaturePadRef.current) {
      familySignaturePadRef.current.clear()
      setFamilySignature(null)
    }
  }

  const handleClearPatientSignature = () => {
    if (patientSignaturePadRef.current) {
      patientSignaturePadRef.current.clear()
      setPatientSignature(null)
    }
  }

  const handleFamilySignatureEnd = () => {
    if (
      familySignaturePadRef.current &&
      !familySignaturePadRef.current.isEmpty()
    ) {
      const dataURL = familySignaturePadRef.current.toDataURL('image/png')
      setFamilySignature(dataURL)
    } else {
      setFamilySignature(null)
    }
  }

  const handlePatientSignatureEnd = () => {
    if (
      patientSignaturePadRef.current &&
      !patientSignaturePadRef.current.isEmpty()
    ) {
      const dataURL = patientSignaturePadRef.current.toDataURL('image/png')
      setPatientSignature(dataURL)
    } else {
      setPatientSignature(null)
    }
  }

  // Check if form is complete
  const isFormComplete = () => {
    return (
      hasReadAndAgreed &&
      !!familySignature &&
      !!patientSignature &&
      familyMemberName.trim().length > 0 &&
      patientName.trim().length > 0
    )
  }

  const handleSubmit = async () => {
    if (!familySignature) {
      setError('Please provide family member/caregiver signature')
      return
    }

    if (!patientSignature) {
      setError('Please provide patient signature')
      return
    }

    if (!familyMemberName.trim()) {
      setError('Please enter family member/caregiver name')
      return
    }

    if (!patientName.trim()) {
      setError('Please enter patient name')
      return
    }

    if (!hasReadAndAgreed) {
      setError('Please confirm that you have read and agreed to all terms')
      return
    }

    if (!patientId) {
      setError('Invalid patient ID')
      return
    }

    setLoading(true)
    setError(null)

    try {
      await ConsentService.submitFamilyConsent({
        patient_id: patientId,
        ipAddress,
        familySignature,
        patientSignature,
        patientName: patientName.trim(),
      })

      // Redirect to patient login page after successful submission
      navigate('/patient-login', { replace: true })
    } catch (err: any) {
      console.error('Error submitting consent:', err)
      setError(
        err.response?.data?.message ||
          'Failed to submit consent. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  const canSubmit = isFormComplete() && !loading

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#FAFBFC',
        py: { xs: 3, md: 4 },
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box
          sx={{
            mb: 4,
            textAlign: 'center',
            bgcolor: 'white',
            p: { xs: 3, md: 4 },
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 4,
            }}
          >
            <Box
              component="img"
              src={LOGO}
              alt="PillarThera Logo"
              sx={{ height: { xs: 60, md: 80 }, width: 'auto' }}
            />
          </Box>
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{
              color: 'primary.main',
              fontSize: { xs: '1.5rem', md: '2rem' },
              mb: 1,
            }}
          >
            Authorized Family Member / Caregiver Access & HIPAA Disclosure
            Consent
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.875rem', md: '1rem' },
            }}
          >
            This consent allows a designated Family Member or Caregiver to
            access and assist with the Patient's PillarThera account.
          </Typography>
        </Box>

        {/* Consent Sections */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 3,
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}
        >
          <Box sx={{ mb: 4 }}>
            {CONSENT_SECTIONS.map((section, index) => (
              <Box
                key={section.id}
                sx={{
                  mb: 3,
                  pb: 3,
                  borderBottom:
                    index < CONSENT_SECTIONS.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{
                    mb: 1,
                    color: 'text.primary',
                    fontSize: { xs: '1rem', md: '1.125rem' },
                  }}
                >
                  {section.title}
                </Typography>
                {section.content.map((paragraph, pIndex) => (
                  <Typography
                    key={pIndex}
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.8,
                      fontSize: { xs: '0.875rem', md: '1rem' },
                    }}
                  >
                    {paragraph}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>

          {/* Final Agreement */}
          <Box
            sx={{
              p: { xs: 2, md: 2.5 },
              bgcolor: hasReadAndAgreed ? 'success.50' : 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: hasReadAndAgreed ? 'success.light' : 'divider',
              transition: 'all 0.2s',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasReadAndAgreed}
                  onChange={e => {
                    setHasReadAndAgreed(e.target.checked)
                    setError(null)
                  }}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    '&.Mui-checked': {
                      color: 'success.main',
                    },
                  }}
                />
              }
              label={
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', md: '1rem' },
                  }}
                >
                  By signing, I agree to these terms.
                </Typography>
              }
            />
          </Box>
        </Paper>

        {/* Signatures Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 3,
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              mb: 3,
              color: 'primary.main',
              fontSize: { xs: '1.125rem', md: '1.25rem' },
            }}
          >
            Signatures
          </Typography>

          <Grid container spacing={3}>
            {/* Family Member / Caregiver Signature */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{
                  mb: 2,
                  color: 'text.primary',
                  fontSize: { xs: '1rem', md: '1.125rem' },
                }}
              >
                Family Member / Caregiver
              </Typography>

              <TextField
                label="Family Member / Caregiver Name *"
                value={
                  isFetchingPatient ? 'Loading...' : familyMemberName || ''
                }
                disabled
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  mb: 2,
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'text.primary',
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', md: '1rem' },
                  },
                }}
              />

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '1rem' } }}
              >
                Signature (draw below) *
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  border: '2px solid',
                  borderColor: familySignature ? 'success.light' : 'divider',
                  borderRadius: 2,
                  p: 1,
                  bgcolor: 'white',
                  display: 'flex',
                  justifyContent: 'center',
                  overflow: 'auto',
                  mb: 1,
                  '& canvas': {
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    cursor: 'crosshair',
                    maxWidth: '100%',
                    height: 'auto',
                  },
                }}
              >
                <SignatureCanvas
                  ref={familySignaturePadRef}
                  canvasProps={{
                    width: isMobile ? 300 : 400,
                    height: 150,
                    className: 'signature-canvas',
                    style: { maxWidth: '100%', height: 'auto' },
                  }}
                  onEnd={handleFamilySignatureEnd}
                />
              </Paper>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearFamilySignature}
                sx={{ mb: 2, textTransform: 'none' }}
              >
                Clear Signature
              </Button>

              <TextField
                label="Date"
                value={dateOfConsent}
                disabled
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'text.primary',
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', md: '1rem' },
                  },
                }}
              />
            </Grid>

            {/* Patient Signature */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{
                  mb: 2,
                  color: 'text.primary',
                  fontSize: { xs: '1rem', md: '1.125rem' },
                }}
              >
                Patient (authorizing access)
              </Typography>

              <TextField
                label="Patient Name *"
                value={patientName}
                onChange={e => setPatientName(e.target.value)}
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                sx={{ mb: 2 }}
              />

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '1rem' } }}
              >
                Patient Signature (authorizing access) *
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  border: '2px solid',
                  borderColor: patientSignature ? 'success.light' : 'divider',
                  borderRadius: 2,
                  p: 1,
                  bgcolor: 'white',
                  display: 'flex',
                  justifyContent: 'center',
                  overflow: 'auto',
                  mb: 1,
                  '& canvas': {
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    cursor: 'crosshair',
                    maxWidth: '100%',
                    height: 'auto',
                  },
                }}
              >
                <SignatureCanvas
                  ref={patientSignaturePadRef}
                  canvasProps={{
                    width: isMobile ? 300 : 400,
                    height: 150,
                    className: 'signature-canvas',
                    style: { maxWidth: '100%', height: 'auto' },
                  }}
                  onEnd={handlePatientSignatureEnd}
                />
              </Paper>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearPatientSignature}
                sx={{ mb: 2, textTransform: 'none' }}
              >
                Clear Signature
              </Button>

              <TextField
                label="Date"
                value={dateOfConsent}
                disabled
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'text.primary',
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', md: '1rem' },
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Consent Details */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 3,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    fontSize: { xs: '0.65rem', md: '0.7rem' },
                  }}
                >
                  IP Address
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', md: '1rem' },
                  }}
                >
                  {isFetchingIp ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={12} />
                      <span>Loading...</span>
                    </Box>
                  ) : (
                    ipAddress
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    fontSize: { xs: '0.65rem', md: '0.7rem' },
                  }}
                >
                  Date & Time
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', md: '1rem' },
                  }}
                >
                  {format(new Date(), 'MM/dd/yyyy h:mm a')}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Paper>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            size="large"
            sx={{
              minWidth: { xs: '100%', md: 300 },
              py: 1.5,
              fontSize: { xs: '0.875rem', md: '1rem' },
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              boxShadow: canSubmit
                ? '0 4px 12px rgba(25, 118, 210, 0.3)'
                : 'none',
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Submit Consent'
            )}
          </Button>
        </Box>
      </Container>
    </Box>
  )
}

export default FamilyMemberConsentPage
